import { Box, Button, Chip } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import {
    PaloConfirmationDialog,
    PaloDetailView,
    PaloHistory,
    PaloModal,
    PaloRecordMenu,
    PaloStep,
    PaloTabs,
} from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { SubPageHandle } from '@synergy/utils';

import * as ActivityActions from '../../../../common/actions/activity-actions';
import * as ContactDetailActions from '../contact-detail/store/contact-detail-global-actions';
import * as ContactDetailOverviewActions from '../contact-detail/overview/store/contact-detail-overview-actions';
import { ContactDetailDocuments } from '../contact-detail/documents/contact-detail-documents';
import { ContactDetailPermissions } from '../contact-detail/permissions/contact-detail-permissions';
import { ContactDetailTabs } from '../contact-detail/models/contact-detail-tabs';
import { DetailContactDetails } from '../../../activity-sidebar/details/detail-contact-details';
import { DetailStream } from '../../../activity-sidebar/details/detail-stream';
import { NoteType } from '../../../../common/models/enums/note-type';
import { PaloContactDetailOverview } from './palo-contact-detail-overview';
import { PaloContactDocuments } from './palo-contact-documents';
import {
    PaloDirtyDot,
    PaloDirtyProvider,
    PaloDirtyScope,
    usePaloSaveOnLeave,
} from '../../../../common/components/palo/palo-dirty-context';
import { PaloRecordDock } from '../../../../common/components/palo/palo-record-dock';
import { PaloRecordNotices } from '../../../../common/components/palo/palo-record-notices';
import { PaloSaveAction, paloDocumentPhase } from '../../../detail-header/palo-main-action';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import {
    contactConditionsErrorContextSelector,
    contactConditionsErrorSelector,
    contactIsLockedSelector,
    contactOverviewSelector,
    contactPermissionsSelector,
    errorTypeSelector,
} from '../contact-detail/store/contact-detail-global-selectors';
import { documentsSelector as contactDocumentsSelector } from '../contact-detail/documents/store/contact-detail-documents-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloRecordKey } from '../../components/use-palo-record-key';
import { useRedirectAfterNewSave } from '../../components/use-redirect-after-new-save';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const PaloContactDetailInner = (): ReactElement => {
    // The document the scopes clear on: only a fresh one from the server means
    // every tab, including the ones not mounted, is provably saved.
    const baseline = useAppSelector(state => state.contactsAndOrganizations.contacts.contactDetail.overview.baseline);
    const subPageRef = useRef<SubPageHandle>(null);
    const { id: contactIdParam } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();

    const dispatch = useThunkDispatch();
    const hasContactAssignedError = useAppSelector(contactConditionsErrorSelector);
    const hasContactAssignedErrorContext = useAppSelector(contactConditionsErrorContextSelector);
    const errorType = useAppSelector(errorTypeSelector);
    const { contact, isValid: overviewIsValid } = useAppSelector(contactOverviewSelector);
    const documentCount = useAppSelector(contactDocumentsSelector).length;
    const { isValid: permissionsIsValid } = useAppSelector(contactPermissionsSelector);
    const contactIsLocked = useAppSelector(contactIsLockedSelector);
    const isNew = isNaN(Number(contactIdParam));
    const contactName = isNew
        ? intl.formatMessage({ id: 'app.contactsAndOrganizations.contact.new' })
        : (contact && `${contact.firstName} ${contact.lastName}`.trim()) || '';

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showLockWarning, setShowLockWarning] = useState(false);
    const [showUnlockWarning, setShowUnlockWarning] = useState(false);

    useRedirectAfterNewSave(ROUTES.contactsAndOrganizations.contacts.detail, contact?.id);
    usePaloSaveOnLeave(subPageRef.current?.submit);

    useEffect(() => {
        dispatch(ActivityActions.setDocumentType(DocumentType.Contact));

        return () => {
            dispatch(ContactDetailActions.resetContactDetails());
            dispatch(ActivityActions.resetDocumentType());
            dispatch(ActivityActions.resetActivities());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (contact?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Contact, contact.id));
        }
    }, [contact, contactIsLocked]);

    useEffect(() => {
        // Whenever the store holds another record than the path names (#2326 R-243).
        if (contactIdParam && !isNaN(+contactIdParam) && contact?.id !== +contactIdParam) {
            dispatch(ContactDetailOverviewActions.fetchContactDetails(+contactIdParam));
        }
    }, [contact?.id, contactIdParam, dispatch]);

    const handleSave = async (): Promise<void> => {
        if (subPageRef.current) {
            await subPageRef.current.validate();
            await subPageRef.current.submit();
        }
        await dispatch(ContactDetailActions.saveContactDetails());
        if (contact?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Contact, contact.id));
        }
    };

    const handleDelete = (): void => {
        setShowDeleteWarning(false);
        dispatch(ContactDetailActions.deleteContact()).then(() =>
            navigate(ROUTES.contactsAndOrganizations.contacts.list)
        );
    };

    const handleLockChanged = (): void => {
        setShowLockWarning(false);
        setShowUnlockWarning(false);
        dispatch(contactIsLocked ? ContactDetailActions.unlockContact() : ContactDetailActions.lockContact());
    };

    const detailBase = ROUTES.contactsAndOrganizations.contacts.detail.replace(
        ':id',
        String(contact?.id ?? contactIdParam)
    );
    const activeSegment = location.pathname.split('/').pop() ?? '';
    const activeTab = (Object.values(ContactDetailTabs) as string[]).includes(activeSegment)
        ? (activeSegment as ContactDetailTabs)
        : ContactDetailTabs.Overview;

    const tabLabel = (tab: string, label: ReactElement, valid = true): ReactElement => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {label}
            {!valid && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'error.main' }} />}
            <PaloDirtyDot scope={tab} />
        </Box>
    );

    // A-1: what a record can have done to it lives in the "⋯" menu — a switch
    // and a red button beside "Speichern" invite the slip they describe. A new
    // record has nothing to lock or delete yet, so the menu stays away.
    const actions = (
        <>
            {!isNew && (
                <PaloRecordMenu
                    label={intl.formatMessage({ id: 'app.common.moreActions' })}
                    actions={[
                        {
                            label: <FormattedMessage id={contactIsLocked ? 'app.label.unlock' : 'app.label.lock'} />,
                            onClick: () => (contactIsLocked ? setShowUnlockWarning(true) : setShowLockWarning(true)),
                        },
                        {
                            label: <FormattedMessage id="app.common.delete" />,
                            danger: true,
                            onClick: () => setShowDeleteWarning(true),
                        },
                    ]}
                />
            )}
            <PaloSaveAction
                phase={paloDocumentPhase(undefined, { saved: !isNew })}
                comparesToServer={true}
                onSave={handleSave}
            />
        </>
    );

    const tabSteps: PaloStep[] = [
        [ContactDetailTabs.Overview, 'details', overviewIsValid, false],
        [ContactDetailTabs.Permissions, 'permissions', permissionsIsValid, true],
        [ContactDetailTabs.Documents, 'documents', true, true],
    ].map(([tab, key, valid, needsId]) => ({
        label: tabLabel(
            tab as string,
            <FormattedMessage id={`app.contactsAndOrganizations.detail.tab.${key}`} />,
            valid as boolean
        ),
        state: activeTab === tab ? 'now' : 'todo',
        disabled: (needsId as boolean) && !contact?.id,
        onClick: () => navigate(`${detailBase}/${tab}`),
    }));

    const tabs = <PaloTabs steps={tabSteps} />;

    return (
        <>
            <PoolDocumentTitle
                titleTranslationId={'app.contactsAndOrganizations.contact.list.title'}
                name={contactName}
            />
            <PaloDetailView
                themeContent={false}
                breadcrumbs={[
                    {
                        label: 'Personen',
                        onClick: () => navigate(ROUTES.contactsAndOrganizations.contacts.list),
                    },
                ]}
                title={contactName}
                status={
                    contactIsLocked ? (
                        <Chip size="small" color="error" label={<FormattedMessage id="app.common.locked" />} />
                    ) : undefined
                }
                actions={actions}
                alert={<PaloRecordNotices noteType={NoteType.Contacts} entityId={contact?.id} />}
                tabs={tabs}
                dock={
                    <PaloRecordDock
                        count={documentCount}
                        // A new contact has nowhere to keep a file yet.
                        attachments={contact?.id ? <PaloContactDocuments /> : undefined}
                        sections={[
                            {
                                key: 'details',
                                label: <FormattedMessage id="app.sidebar.details" />,
                                content: <DetailContactDetails />,
                            },
                        ]}
                        collapsedLabel={<FormattedMessage id="app.sidebar.details" />}
                    />
                }
                history={
                    <PaloHistory>
                        <DetailStream documentType={DocumentType.Contact} documentId={contact?.id} />
                    </PaloHistory>
                }
            >
                <Routes>
                    <Route
                        path={ContactDetailTabs.Overview}
                        element={
                            <PaloDirtyScope name={ContactDetailTabs.Overview}>
                                <PaloContactDetailOverview ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path={ContactDetailTabs.Permissions}
                        element={
                            <PaloDirtyScope name={ContactDetailTabs.Permissions}>
                                <ContactDetailPermissions ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={ContactDetailTabs.Documents} element={<ContactDetailDocuments />} />
                    <Route
                        path="*"
                        element={<Navigate to={{ pathname: ContactDetailTabs.Overview }} replace={true} />}
                    />
                </Routes>
            </PaloDetailView>

            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.contact.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.contact.deleteWarning"
                    values={{ name: contactName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showLockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.contact.lock" />}
                action={<FormattedMessage id="app.label.lock" />}
                onCancel={() => setShowLockWarning(false)}
                onConfirm={handleLockChanged}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.contact.lockWarning"
                    values={{ name: contactName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showUnlockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.contact.unlock" />}
                action={<FormattedMessage id="app.label.unlock" />}
                onCancel={() => setShowUnlockWarning(false)}
                onConfirm={handleLockChanged}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.contact.unlockWarning"
                    values={{ name: contactName }}
                />
            </PaloConfirmationDialog>
            <PaloModal
                isVisible={hasContactAssignedError}
                title={<FormattedMessage id="app.contactsAndOrganizations.contact.conditionError" />}
                handleClose={() => dispatch(ContactDetailActions.resetContactDetailsConditionError())}
                width="xs"
                themeContent
                actionChildren={
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => dispatch(ContactDetailActions.resetContactDetailsConditionError())}
                    >
                        <FormattedMessage id="app.common.okAgreed" />
                    </Button>
                }
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.contact.conditionErrorMessage"
                    values={{
                        type: hasContactAssignedErrorContext?.accountType,
                        name: hasContactAssignedErrorContext?.accountName,
                        errorType,
                    }}
                />
            </PaloModal>
        </>
    );
};

export const PaloContactDetail = (): ReactElement => {
    const { id } = useParams<{ id: string }>();
    const recordKey = usePaloRecordKey(id);
    // A fresh document from the server is the one moment at which every part
    // of the mask, mounted or not, is provably saved.
    const loadedDocument = useAppSelector(
        state => state.contactsAndOrganizations.contacts.contactDetail.overview.baseline
    );

    return (
        <PaloDirtyProvider
            key={recordKey}
            basePath={ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', String(id))}
            loadedDocument={loadedDocument}
        >
            <PaloContactDetailInner />
        </PaloDirtyProvider>
    );
};
